// Fetch the signed-in user's own Discord profile (notably the username slug used
// as the want-page URL handle) from the server, given a Firebase ID token.
//
// This is the path that lets an ALREADY-signed-in session learn its username
// without re-authenticating: the slug only arrives in the discord_oauth response
// at sign-in time, so a session that predates username caching has nothing
// cached. The auth store calls this on auth-ready and caches the result, so the
// sidebar links straight to /u/@<username>/wantlist (never @me).
//
// The token is injected (the auth store calls this from its own listener with
// the user it was handed), and apiFetch sends it as given. `fetchFn` is
// injectable so this unit-tests without a network.

import { API_ENDPOINTS } from './endpoints.js';
import { apiFetch } from './api-fetch.js';

/**
 * @param {string|null} idToken Firebase ID token for the Authorization header.
 * @param {import('../types.js').FetchLike} [fetchFn]
 * @returns {Promise<{discordUsername:string|null, discordId:string|null, displayName:string|null}|null>}
 *   The profile, or null when there's no token / the request fails / the user
 *   has no linked Discord account. Never throws.
 */
export async function fetchMyProfile(idToken, fetchFn = fetch) {
  if (!idToken) return null;
  try {
    const res = await apiFetch(
      API_ENDPOINTS.refresh_profile_api,
      { method: 'GET' },
      { token: idToken, fetchFn }
    );
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    if (!data || !data.success) return null;
    return {
      discordUsername: data.discordUsername ?? null,
      discordId: data.discordId ?? null,
      displayName: data.displayName ?? null,
    };
  } catch {
    return null;
  }
}
